// Settings utility functions

const SETTINGS_STORAGE_KEY = 'dialverse_settings'

// Default settings
export const DEFAULT_SETTINGS = {
  appearance: {
    darkMode: 'auto', // 'auto', 'light', 'dark'
    fontSize: 'medium', // 'small', 'medium', 'large'
    compactMode: false,
  },
  notifications: {
    emailNotifications: true,
    pushNotifications: true,
    soundEnabled: true,
    marketingEmails: false,
  },
  privacy: {
    dataCollection: true,
    analytics: true,
    crashReporting: true,
  },
  accessibility: {
    highContrast: false,
    reducedMotion: false,
    screenReader: false,
  },
  language: {
    locale: 'en-US',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
}

// Load settings from localStorage
export const loadSettings = () => {
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Merge with defaults to ensure all settings exist
      return mergeSettings(DEFAULT_SETTINGS, parsed)
    }
  } catch (error) {
    console.error('Error loading settings:', error)
  }
  return DEFAULT_SETTINGS
}

// Save settings to localStorage
export const saveSettings = (settings) => {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings))
    return true
  } catch (error) {
    console.error('Error saving settings:', error)
    return false
  }
}

// Merge settings (deep merge)
export const mergeSettings = (defaults, userSettings) => {
  const merged = { ...defaults }

  Object.keys(userSettings).forEach((category) => {
    if (merged[category] && typeof userSettings[category] === 'object') {
      merged[category] = { ...merged[category], ...userSettings[category] }
    } else {
      merged[category] = userSettings[category]
    }
  })

  return merged
}

// Update a specific setting
export const updateSetting = (currentSettings, category, key, value) => {
  const updated = {
    ...currentSettings,
    [category]: {
      ...currentSettings[category],
      [key]: value,
    },
  }

  saveSettings(updated)
  return updated
}

// Reset settings to defaults
export const resetSettings = () => {
  localStorage.removeItem(SETTINGS_STORAGE_KEY)
  return DEFAULT_SETTINGS
}

// Get timezone options
export const getTimezoneOptions = () => {
  const timezones = Intl.supportedValuesOf('timeZone')
  return timezones.map((tz) => ({
    value: tz,
    label: tz.replace(/_/g, ' '),
  }))
}

// Get locale options
export const getLocaleOptions = () => [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es-ES', label: 'Español' },
  { value: 'fr-FR', label: 'Français' },
  { value: 'de-DE', label: 'Deutsch' },
  { value: 'it-IT', label: 'Italiano' },
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'ja-JP', label: '日本語' },
  { value: 'ko-KR', label: '한국어' },
  { value: 'zh-CN', label: '中文 (简体)' },
]

// Get font size options
export const getFontSizeOptions = () => [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
]

// Validate settings
export const validateSettings = (settings) => {
  const errors = []

  // Check required categories
  const requiredCategories = ['appearance', 'notifications', 'privacy', 'accessibility', 'language']
  requiredCategories.forEach((category) => {
    if (!settings[category]) {
      errors.push(`Missing required category: ${category}`)
    }
  })

  // Validate specific values
  if (
    settings.appearance?.fontSize &&
    !['small', 'medium', 'large'].includes(settings.appearance.fontSize)
  ) {
    errors.push('Invalid font size value')
  }

  if (
    settings.appearance?.darkMode &&
    !['auto', 'light', 'dark'].includes(settings.appearance.darkMode)
  ) {
    errors.push('Invalid dark mode value')
  }

  return errors
}
